<template>
    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Help Desk Ticket Settings</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
            Configure ticket categories, SLA policies, and helpdesk settings
          </p>
        </div>
      </div>

      <!-- Settings Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Help Desk Services Card -->
        <Link :href="route('settings.tickets.categories.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Help Desk Services</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Manage ticket categories and subcategories to organize and classify support requests
              </p>
            </div>
          </Card>
        </Link>

        <!-- Help Desk SLA Policies Card -->
        <Link :href="route('settings.tickets.sla.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Help Desk SLA Policies</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Define service level agreements with response and resolution time targets for tickets
              </p>
            </div>
          </Card>
        </Link>

        <!-- Ticket Notification Policies Card -->
        <Link :href="route('ticket-notification-policies.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Notification & Routing Policies</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Configure who gets notified and routed based on ticket category, priority, location, and IT equipment
              </p>
            </div>
          </Card>
        </Link>

        <!-- IT Equipment Management Card -->
        <Link v-if="subscriptionFeatures.equipment_module" :href="route('it-equipment.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">IT Equipment Management</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Manage IT equipment inventory, categories, user assignments, and equipment tracking
              </p>
            </div>
          </Card>
        </Link>
      </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import route from '@/Utils/route';

// Access subscription features from Inertia shared props
const page = usePage();
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });
</script>
